import { normalise } from "$root/functional-areas/instruments-editor/builder";
import { labDiv, labMul, labSum } from "../../../utils/math";
import { isCompositionExactly100 } from "../../../common";
import { useDecimalPlaces } from "$root/functional-areas/user/useDecimalPlaces";
import { readAtom, useAtomValue, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import BigNumber from "bignumber.js";
import type { ICellRendererParams } from "ag-grid-community";
import { Map, OrderedMap } from "immutable";
import {
	PortfolioLabContext,
	type PortfolioLabComposition,
	type PortfolioLabContextProps,
	type PortfolioLabInvestmentItem,
} from "../../../ctx";
import type { CompositionGroupBy } from "../../../state";
import { DropdownMenu, Button } from "@mdotm/shadcn/mdotm/components";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import type { UserInstrumentDto } from "@eu.mdotm.api/sphere";
import type { FunctionComponent } from "react";
import type { AsyncStateId } from "@mdotm/utils/promise";
import { spawnNormalizeDialog } from "../../NormalizeDialog";
import { buildCompositionRows, type MergedCompositionRow } from "../rows";
import { createNormalizeEditor, rescaleCompositionMonetaryWeights } from "../weight-edit";
import { deriveCompositionViewMode } from "../grid-sync";
import { syncComposition } from "../persistence";
import { hasEditableAum } from "../editability";

function compositionKey(c: PortfolioLabComposition) {
	return c.type === "instrument" ? c.tickerName : c.uuid;
}

type LabNormalizeAndAumCellRendererProps = ICellRendererParams<MergedCompositionRow> & {
	virtualUuid: string;
};

export function LabNormalizeAndAumCellRenderer({ virtualUuid }: LabNormalizeAndAumCellRendererProps) {
	const ctx = useMandatoryContext(PortfolioLabContext);
	const latestInvestments = useAtomValue(ctx.investments$);
	const investment = latestInvestments.find((i) => i.virtualUuid === virtualUuid);
	const { customerDecimalPlacesPreferences } = useDecimalPlaces();

	if (!investment) {
		return <div />;
	}

	// Summed from `investments$`, not from the pinned row's `totalWeight`: every column value on the
	// totals row is null, so AG Grid's refresh skips these cells (equal values, and each colDef has a
	// valueGetter) and their `data` prop stays at the composition as it was when the row first rendered.
	// The same condition as the totals cell's colour, so Solve Issue shows exactly when the total reads invalid.
	if (isCompositionExactly100(investment.currentComposition, customerDecimalPlacesPreferences)) {
		return <div />;
	}

	return (
		<div className="flex items-center gap-1 h-full">
			<DropdownMenu
				Trigger={SolveIssueButton}
				actions={[
					{
						label: "Normalize weights",
						onClick() {
							onNormalize({ virtualUuid, ctx, weightTotalPrecision: customerDecimalPlacesPreferences });
						},
					},
					// Updating the AUM rescales it from the current total weight — with no (or zero) AUM
					// there is nothing to rescale, so hide it.
					...(hasEditableAum(investment)
						? [
								{
									label: "Update Aum",
									onClick() {
										updateAum({ virtualUuid, ctx });
									},
								},
							]
						: []),
				]}
			/>
		</div>
	);
}

const SolveIssueButton: FunctionComponent<
	{
		state: AsyncStateId;
	} & Record<string, unknown> &
		object
> = ({ state: _state, ...props }) => {
	return (
		<Button size="x-small" palette="secondary-danger" {...props}>
			Solve Issue
		</Button>
	);
};

function mergedInstrumentPool(investments: PortfolioLabInvestmentItem[]) {
	return Map<string, UserInstrumentDto>(investments.flatMap((inv) => [...inv.tickerInfoPool]));
}

function buildNormalizeDialogRows({
	target,
	investments,
	groupBy,
}: {
	target: PortfolioLabInvestmentItem;
	investments: PortfolioLabInvestmentItem[];
	groupBy: CompositionGroupBy;
}) {
	return buildCompositionRows({
		composition: target.currentComposition,
		initialComposition: target.initialComposition,
		instrumentPool: mergedInstrumentPool(investments),
		viewMode: deriveCompositionViewMode(groupBy),
		classificationUuid: groupBy.secondLevel,
		investmentName: target.name,
	});
}

async function onNormalize({
	virtualUuid,
	ctx,
	weightTotalPrecision,
}: {
	virtualUuid: string;
	ctx: PortfolioLabContextProps;
	weightTotalPrecision: number;
}) {
	const investments = readAtom(ctx.investments$);
	const decimals = readAtom(ctx.decimals$);
	const groupBy = readAtom(ctx.compositionGroupBy$);

	const target = investments.find((i) => i.virtualUuid === virtualUuid);
	if (!target) {
		return;
	}

	spawnNormalizeDialog({
		rows: buildNormalizeDialogRows({ target, investments, groupBy }),
		decimals,
		async onSubmitAsync(selected) {
			if (selected.length === 0) {
				return;
			}
			// The dialog hands back stable instrument keys, and the math runs here against a fresh
			// read: the composition may have moved since the dialog opened (an in-flight sync settling,
			// SSE update), and normalizing a stale snapshot would silently revert those changes.
			const latest = readAtom(ctx.investments$);
			const before = latest.find((inv) => inv.virtualUuid === virtualUuid);
			if (!before) {
				return;
			}
			const updated = createNormalizeEditor({
				composition: before.currentComposition,
				instrumentPool: mergedInstrumentPool(latest),
				groupBy: groupBy.secondLevel,
				aum: before.aum,
			}).normalizeFlatInstruments(selected, weightTotalPrecision);
			if (updated === before.currentComposition) {
				return;
			}
			ctx.undoRedo.snapshot({
				virtualUuid,
				composition: before.currentComposition,
				aum: before.aum,
				initialComposition: before.initialComposition,
				initialAum: before.initialAum,
			});
			writeAtom(ctx.investments$, (items) =>
				items.map(
					(i): PortfolioLabInvestmentItem =>
						i.virtualUuid === virtualUuid
							? {
									...i,
									currentComposition: updated,
								}
							: i,
				),
			);
			// The atom write above re-derives the grid's rowData; persistence goes through the investment's
			// write lane so a normalize can never overtake earlier edits on the server (T6). The column
			// header reads `investments$`, so its sync-error icon repaints without an imperative refresh.
			await syncComposition(ctx, virtualUuid);
		},
	});
}

function updateAum({ virtualUuid, ctx }: { virtualUuid: string; ctx: PortfolioLabContextProps }) {
	const investment = readAtom(ctx.investments$).find((inv) => inv.virtualUuid === virtualUuid);
	if (!investment || !hasEditableAum(investment)) {
		return;
	}
	const decimals = readAtom(ctx.decimals$);
	const totalWeight = labSum(investment.currentComposition, (c) => c.weight);

	const composition = investment.currentComposition;

	// Update-AUM rebases `initialComposition`/`initialAum` below — snapshot them too so an undo
	// restores the previous delta/Reset baseline (T5).
	ctx.undoRedo.snapshot({
		virtualUuid,
		composition: investment.currentComposition,
		aum: investment.aum,
		initialComposition: investment.initialComposition,
		initialAum: investment.initialAum,
	});

	const allocationMap = OrderedMap<string, BigNumber>(composition.map((c) => [compositionKey(c), BigNumber(c.weight)]));

	const normalizedMap = normalise({
		distributeTo: allocationMap.keySeq(),
		allocation: allocationMap,
		decimalPlaces: decimals,
	});

	const newAum = labMul(labDiv(totalWeight, 100), investment.aum);
	const normalizedComposition = composition.map((c) => {
		const w = normalizedMap.get(compositionKey(c));
		return w != null ? { ...c, weight: w.toNumber() } : c;
	});
	const newComposition = rescaleCompositionMonetaryWeights(normalizedComposition, newAum);
	writeAtom(ctx.investments$, (items) =>
		items.map((i) =>
			i.virtualUuid === virtualUuid
				? {
						...i,
						currentComposition: newComposition,
						aum: newAum,
						initialComposition: newComposition,
						initialAum: newAum,
						baselineUnsaved: true,
					}
				: i,
		),
	);

	// The atom write above re-derives the grid's rowData; persistence goes through the write lane (T6).
	// The new AUM label and the sync-error icon repaint from `investments$` — the column header
	// subscribes to it, and an imperative refreshHeader() would remount that header.
	void syncComposition(ctx, virtualUuid);
}
